import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import { useRepliesForCommentInfiniteQuery } from "src/api/client/comment-v2.api";
import { EnumCommentStatus } from "src/api/client/comment-v2.api.types";
import { EnumActivityType } from "src/api/client/generic-activity.api";
import { useLikeActivityMutation } from "src/api/client/like.api";
import { Button } from "src/components/base/button";
import { WfLinkGhost } from "src/components/base/wf-link";
import { LikeButton } from "src/components/common/buttons/like-button/like-button";
import { EnumLikeableEntityType } from "src/components/common/buttons/like-button/like-button.types";
import { ParentCommentPreview } from "src/components/common/comments-area/components/parent-comment-preview";
import { createInitialRepliesPageData } from "src/components/common/comments-area/utils/create-initial-replies-page-data";
import { getParentPreviewInfo } from "src/components/common/comments-area/utils/get-parent-preview-info";
import { removeDuplicates } from "src/components/common/comments-area/utils/remove-duplicates";
import { resolveCommentsLength } from "src/components/common/comments-area/utils/resolve-comments-length";
import { ExpandableText } from "src/components/common/expandable-text";
import { TraderAvatar } from "src/components/common/trader-avatar";
import { IconCaret } from "src/components/icons";
import { useNavHeight } from "src/components/layout/hooks/use-nav-height";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { IUser } from "src/types/common";
import { TComment } from "./comment.types";
import { CommentContextMenu } from "./components/comment-context-menu";
import { MessageStatusIndicator } from "./components/message-status-indicator";

export type TReplyParams = { commentId: string; message: string };
export type TEditParams = { parentCommentId: string | null; message: string; parentPreviewMessage: string | undefined; commentId: string };

export interface ICommentV2Props {
    comment: TComment;
    onReply: (params: TReplyParams) => void;
    onEdit: (params: TEditParams) => void;
    onDelete?: () => void;
    commentsInThread?: TComment[];
    currentUserId: IUser["id"] | undefined;
    showReplies?: boolean;
    indent?: boolean;
    entityId: string;
    isCurrentUserQualifiedCommenter: boolean;
}

const COMMENT_MESSAGE_TEST_ID = "comment-message";
const COMMENT_REPLIES_TEST_ID = "comment-replies";
const COMMENT_REPLY_BUTTON_TEST_ID = "comment-reply-button";
const COMMENT_TEST_ID = "comment";

const toNonEmptyString = (value: string | null | undefined) => {
    if (!value) {
        return undefined;
    }
    return value;
};

export const CommentV2 = (props: ICommentV2Props) => {
    const { comment, onReply, onEdit, onDelete, showReplies, indent, commentsInThread, isCurrentUserQualifiedCommenter, currentUserId, entityId } =
        props;
    const { t } = useTranslation("comment");
    const getUserName = useGetUserName();
    const { buildTraderProfileLink } = useLinkBuilder();

    const { data, fetchNextPage, hasNextPage } = useRepliesForCommentInfiniteQuery(comment.commentId, {
        enabled: false,
        initialData: () => createInitialRepliesPageData(comment),
    });

    const likeActivity = useLikeActivityMutation();
    const trackCommentLiked = () =>
        likeActivity({
            activityType: EnumActivityType.CommentLiked,
            parameter: JSON.stringify(
                {
                    commentId: comment.commentId,
                    rootCommentId: comment.rootCommentId,
                    parentCommentId: comment.parentCommentId,
                },
                (k, v) => v ?? undefined
            ),
        });

    const replies = data?.pages.flatMap(item => item.comments);
    const repliesCount = comment?.replies?.repliesCount && replies ? comment.replies.repliesCount - resolveCommentsLength(replies) : 0;
    const isCommentDeleted = comment.status === EnumCommentStatus.Deleted;

    const { qualifiedCommenter } = comment;

    const shouldShowLoadMoreButton = hasNextPage && comment.replies && repliesCount > 0;
    const shouldShowReplies = showReplies && replies;
    const showReplyButton = !isCommentDeleted && currentUserId && isCurrentUserQualifiedCommenter;
    const showContextMenu = currentUserId === qualifiedCommenter.id && !isCommentDeleted;
    const traderProfileLink = buildTraderProfileLink(qualifiedCommenter.nickName);

    const parentPreviewInfo = getParentPreviewInfo({ comments: commentsInThread, parentCommentId: comment.parentCommentId, t });
    const { NAV_HEIGHT_VARIATION } = useNavHeight();

    const scrollMarginTopValue = NAV_HEIGHT_VARIATION.map(x => (x + 1) * -1);
    const username = getUserName(qualifiedCommenter);

    return (
        <Box data-test-id={COMMENT_TEST_ID} data-parent-comment-id={toNonEmptyString(comment.parentCommentId)}>
            <Box id={props.comment.commentId} pos="relative" top={scrollMarginTopValue}></Box>
            <Flex mb={1} justifyContent="space-between">
                <Flex>
                    <TraderAvatar href={traderProfileLink} size="sm" trader={qualifiedCommenter} mr={1} />
                    <Box>
                        {createUserLink(username, qualifiedCommenter.isDeleted, traderProfileLink)}
                        <MessageStatusIndicator comment={comment} />
                    </Box>
                </Flex>
                <Box>
                    {showContextMenu ? (
                        <CommentContextMenu
                            comment={comment}
                            entityId={entityId}
                            onEditOpen={() =>
                                onEdit({
                                    commentId: comment.commentId,
                                    message: comment.message,
                                    parentPreviewMessage: parentPreviewInfo?.message,
                                    parentCommentId: comment.parentCommentId,
                                })
                            }
                        />
                    ) : null}
                </Box>
            </Flex>
            <Box ml={indent ? 6 : 0}>
                <ParentCommentPreview comment={comment} parentMessage={parentPreviewInfo?.message} parentDeleted={parentPreviewInfo?.deleted} />
                {isCommentDeleted ? (
                    <Text color="gray.600" fontSize="sm" data-test-id={COMMENT_MESSAGE_TEST_ID}>
                        {comment.deletedByWikifolio ? t("title.comment-was-deleted-by-wikifolio") : t("title.comment-was-deleted-by-trader")}
                    </Text>
                ) : (
                    <ExpandableText
                        wordBreak="break-word"
                        formattingType="userHtml"
                        noOfLines={6}
                        text={comment.message}
                        maxW="100%"
                        data-test-id={COMMENT_MESSAGE_TEST_ID}
                    />
                )}
                <Flex mt={0.5}>
                    <LikeButton
                        size="sm"
                        disabled={isCommentDeleted}
                        likeInfo={{ didLike: comment.userDidLike, count: comment.likesCount }}
                        entityReferences={[{ entityId: comment.commentId, entityType: EnumLikeableEntityType.Comment }]}
                        onClick={trackCommentLiked}
                    />
                    {showReplyButton && (
                        <Button
                            _focus={{ bg: undefined }}
                            onClick={() => {
                                onReply({ commentId: comment.commentId, message: comment.message });
                            }}
                            variant="ghost"
                            size="sm"
                            data-test-id={COMMENT_REPLY_BUTTON_TEST_ID}
                        >
                            {t("buttons.reply")}
                        </Button>
                    )}
                </Flex>
                {shouldShowReplies && (
                    <>
                        <VStack spacing={2} mt={2} mb={1} w="100%" align="stretch" data-test-id={COMMENT_REPLIES_TEST_ID}>
                            {removeDuplicates(replies).map(c => (
                                <CommentV2
                                    entityId={entityId}
                                    isCurrentUserQualifiedCommenter={isCurrentUserQualifiedCommenter}
                                    currentUserId={currentUserId}
                                    key={c.commentId}
                                    comment={c}
                                    commentsInThread={[comment, ...replies]}
                                    onReply={onReply}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    showReplies={false}
                                />
                            ))}
                        </VStack>
                        {shouldShowLoadMoreButton && (
                            <Button
                                mt={0.5}
                                variant="ghost"
                                size="sm"
                                leftIcon={<IconCaret boxSize={3} />}
                                display="flex"
                                onClick={() => fetchNextPage()}
                            >
                                {t("buttons.load-more-replies", { 0: repliesCount })}
                            </Button>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

const createUserLink = (username: string, isDeleted = false, href: string) => {
    if (href && !isDeleted) {
        return (
            <WfLinkGhost href={href} mb="0.5" fontWeight="bold" fontSize="sm">
                {username}
            </WfLinkGhost>
        );
    }

    return (
        <Text fontSize="sm" mb="0.5" fontWeight="bold">
            {username}
        </Text>
    );
};
